/**
 * Pure helpers used across the Overview page + its component split.
 *
 * Kept stateless and side-effect-free so they can be imported from
 * any of the broken-out card / tile components without dragging in
 * React or React Query. If a helper grows to need either, move it
 * into a hook file under `lib/` — don't pollute this file.
 */

import type { DateRange } from "@/lib/date-range";

/** Parse two ISO yyyy-mm-dd strings into local-midnight Dates, or
 *  null if either side fails basic shape / number checks. Using
 *  `new Date("2025-01-15")` would parse as UTC midnight and shift a
 *  day in negative-offset timezones — the DateRangePicker expects
 *  local-midnight Date objects, so we construct them explicitly. */
export function parseYmdSafe(from: string, to: string): DateRange | null {
  const re = /^(\d{4})-(\d{2})-(\d{2})$/;
  const mf = re.exec(from);
  const mt = re.exec(to);
  if (!mf || !mt) return null;
  const f = new Date(Number(mf[1]), Number(mf[2]) - 1, Number(mf[3]));
  const t = new Date(Number(mt[1]), Number(mt[2]) - 1, Number(mt[3]));
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return null;
  return { from: f, to: t };
}

/** Tolerant numeric coercion — Decimal columns from the backend
 *  arrive as strings. Returns 0 (not null) so totals computed by
 *  reducing over rows behave naturally even when some values are
 *  missing. Use the metric registry's own `num()` instead when
 *  null is the right "missing" answer. */
export function parseNumberOrZero(
  v: string | number | null | undefined
): number {
  if (v == null) return 0;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

/** Minimal iso-alpha2 → flag emoji.
 *  Converts each of the two ASCII letters to the corresponding
 *  regional-indicator symbol (U+1F1E6 + (ord - 'A')). Falls back to
 *  "🌐" for the empty / unknown bucket. */
export function flagFor(country: string): string {
  const c = country.trim().toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return "🌐";
  const base = 0x1f1e6;
  const a = base + (c.charCodeAt(0) - 65);
  const b = base + (c.charCodeAt(1) - 65);
  return String.fromCodePoint(a) + String.fromCodePoint(b);
}

/** Human-readable country name, falling back to the ISO code itself.
 *  We avoid shipping a full locale table — Intl.DisplayNames handles
 *  every modern browser we support and the result follows the user's
 *  UI language automatically.
 *
 *  Empty country = Apple's "Low Volume" privacy bucket. Apple collapses
 *  any per-country slice below 100 impressions/day into a single
 *  `other: true` row with `countryCode: null` — we preserve that row
 *  and label it here so the user sees the exact portion of spend that
 *  isn't attributable to a specific country. */
const COUNTRY_NAMES = new Intl.DisplayNames(["en"], { type: "region" });
export function countryName(country: string): string {
  const c = country.trim().toUpperCase();
  if (!c) return "Other (low volume)";
  try {
    return COUNTRY_NAMES.of(c) ?? c;
  } catch {
    return c;
  }
}

/** "just now" / "5 sec ago" / "12 min ago" / "3 hr ago" / "2d ago".
 *  Used by the refresh control + anywhere a "last updated" hint is
 *  shown. Caps at days; bigger gaps shouldn't happen on Overview
 *  (the data fetcher refreshes on every visit). */
export function relativeTime(ts: number): string {
  const delta = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (delta < 10) return "just now";
  if (delta < 60) return `${delta} sec ago`;
  const mins = Math.floor(delta / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Two-letter avatar fallback for an app's name. Strips punctuation,
 *  picks first letter of first two words, uppercases. */
export function initialsFor(name: string): string {
  const parts = name
    .replace(/[^a-zA-Z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  return (a + b).toUpperCase().slice(0, 2);
}

/** Deterministic gradient swatch by app id. The order is stable so
 *  the same app keeps the same colour across reloads — important so
 *  the user builds a visual identity for each of their apps over
 *  time without us having to persist a colour choice. */
export function tintFor(id: number): string {
  const palettes = [
    "linear-gradient(135deg,#6e62ff,#4a40cc)",
    "linear-gradient(135deg,#1f9d55,#0e7b45)",
    "linear-gradient(135deg,#c88407,#8d5e07)",
    "linear-gradient(135deg,#2f66f2,#1440a8)",
    "linear-gradient(135deg,#d64242,#b23030)",
    "linear-gradient(135deg,#5c6275,#2a2f3d)",
    "linear-gradient(135deg,#2fb46f,#139454)",
    "linear-gradient(135deg,#8d85ff,#5a4ff0)",
  ];
  return palettes[id % palettes.length] ?? palettes[0]!;
}

/** "Apr 25" — short axis label format. UTC because the date string
 *  itself is calendar-day-keyed by the backend; rendering in local
 *  time would shift labels in negative-offset timezones. */
/** Компактная подпись отрезка: «Aug 1–28» внутри месяца, «Jul 28–Aug 3»
 *  на стыке. Повторять месяц дважды нельзя — подпись не влезает в поле и
 *  обрезается по краю графика; замерено на «Aug 1–Aug 28». */
export function shortRange(fromIso: string, toIso: string): string {
  const a = new Date(fromIso);
  const b = new Date(toIso);
  const sameMonth =
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth();
  return sameMonth
    ? `${shortDate(fromIso)}–${b.getUTCDate()}`
    : `${shortDate(fromIso)}–${shortDate(toIso)}`;
}

export function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** "Sat, Apr 25, 2026" — full hover label format, same UTC story
 *  as `shortDate`. */
export function longDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Time-of-day greeting for the page header. Local time on purpose —
 *  the user's wall clock is what feels right for "good morning". */
export function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
